/**
 * Cached fetch utility for Enhanced Profiles Web
 *
 * Wraps native fetch with sessionStorage caching for GET requests.
 * On page refresh, cached responses are served if not expired,
 * avoiding redundant API calls for the same data.
 *
 * Cache is stored in sessionStorage (cleared on browser/tab close).
 * TTL defaults to 5 minutes.
 */

const CACHE_PREFIX = 'ep_cache_';
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: unknown;
  timestamp: number;
  ttl: number;
}

/**
 * Generate a cache key from the URL
 */
function getCacheKey(url: string): string {
  return `${CACHE_PREFIX}${url}`;
}

/**
 * Get cached response if valid (not expired)
 */
function getFromCache(url: string): unknown | null {
  try {
    const key = getCacheKey(url);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const entry: CacheEntry = JSON.parse(raw);
    const now = Date.now();

    if (now - entry.timestamp > entry.ttl) {
      sessionStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Store response in cache
 */
function setInCache(url: string, data: unknown, ttl: number): void {
  try {
    const key = getCacheKey(url);
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      ttl
    };
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    // sessionStorage full or unavailable — silently skip caching
    console.warn('Cache write failed:', e);
  }
}

/**
 * Invalidate all cached entries (e.g., after a mutation)
 */
export function invalidateCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Invalidate cached entries matching a URL pattern
 *
 * @param pattern - Substring to match against cached URLs
 */
export function invalidateCacheByPattern(pattern: string): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX) && key.includes(pattern)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

interface CachedFetchOptions extends RequestInit {
  /** Time-to-live in milliseconds. Defaults to 5 minutes. */
  cacheTtl?: number;
  /** Skip cache and always fetch fresh. Defaults to false. */
  skipCache?: boolean;
}

/**
 * Fetch with sessionStorage caching for GET requests.
 *
 * - Only GET requests are cached (POST/PUT/DELETE bypass cache).
 * - Returns a Response-like object with `.ok`, `.status`, `.json()`, `.statusText`.
 * - On cache hit, returns immediately without network call.
 *
 * @param url - The request URL
 * @param options - Fetch options + caching options (cacheTtl, skipCache)
 * @returns Promise<Response>
 *
 * @example
 * ```tsx
 * import { cachedFetch } from '../../utils/cachedFetch';
 *
 * const res = await cachedFetch(url, { headers });
 * if (res.ok) {
 *   const data = await res.json();
 * }
 * ```
 */
export async function cachedFetch(
  url: string,
  options?: CachedFetchOptions
): Promise<Response> {
  const method = (options?.method || 'GET').toUpperCase();
  const ttl = options?.cacheTtl ?? DEFAULT_TTL_MS;
  const skipCache = options?.skipCache ?? false;

  // Only cache GET requests
  if (method !== 'GET' || skipCache) {
    return fetch(url, options);
  }

  // Check cache first
  const cached = getFromCache(url);
  if (cached !== null) {
    // Return a synthetic Response with cached data
    return new Response(JSON.stringify(cached), {
      status: 200,
      statusText: 'OK (cached)',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Cache miss — make the real request
  const response = await fetch(url, options);

  // Only cache successful responses
  if (response.ok) {
    try {
      const cloned = response.clone();
      const data = await cloned.json();
      setInCache(url, data, ttl);
    } catch {
      // Response wasn't JSON — skip caching
    }
  }

  return response;
}
